import { useEffect, useMemo, useState } from "react"
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { Check, ChevronLeft, Clock, Folder, Inbox, Paperclip, Pencil, Send, ShieldAlert, Trash2, UserRound, X } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { postfachQuery } from "./postfach"
import { fetchEmails } from "./data"
import { cleanSender, fmtDate } from "./format"
import { HERKUNFT_MIME, verschiebenMitUndo } from "./FolderItems"
import { istUngelesen, zeilenKlasse } from "./gelesen"

/**
 * Ordner im neuen Postfach (Paul 28.10.2026).
 *
 * „Wo sehe ich gesendete Mails? Und die originalen IMAP-Ordner? Wo die mir
 * zugeordneten?“ und dann: „die Ordner ganz unten machen es unmöglich, Mails
 * zu verschieben, insbesondere mehrere … vorher hatten wir Drag & Drop. Man
 * muss die Ordner anzeigen, aber auch gut ausblenden können.“
 *
 * Deshalb eine eigene Spalte ganz links: Ansichten und alle IMAP-Ordner,
 * jeder Ordner ist Ziel zum Ablegen (eine oder mehrere Mails), ein Klick
 * öffnet ihn als Liste. Die Spalte lässt sich zuklappen (je Browser gemerkt)
 * und klappt beim Ziehen von selbst auf.
 */

/** Wie das Backend (`database/emails.py` SPAM_ORDNER_FILTER): alle Spam-/Junk-Ordner. */
const SPAM = "__SPAM__"
const ZU = "pk_ordner_zu"

export type Liste =
  | { art: "meine" | "wartet" | "gesendet"; titel: string }
  | { art: "ordner"; ordner: string; titel: string }

export function listeGleich(a: Liste | null, b: Liste): boolean {
  if (!a || a.art !== b.art) return false
  return a.art !== "ordner" || (b.art === "ordner" && a.ordner === b.ordner)
}

// Gleiche Auswahl wie die bisherige Ordnerleiste: Standardordner stehen als
// eigene Einträge da, der Rest deutsch sortiert.
const STANDARD = ["INBOX", "SENT", "SENT ITEMS", "TRASH", "PAPIERKORB", "DRAFTS",
                  "ENTWUERFE", "ENTWÜRFE", "JUNK", "SPAM"]

export function ordnerName(f: string): string {
  return f.toUpperCase() === "INBOX" ? "Posteingang" : f
}

export function sichtbareOrdner(alle: string[]): string[] {
  return alle
    .filter((f) => {
      const u = f.toUpperCase()
      return !STANDARD.includes(u) && !u.startsWith("[GMAIL]") && !/(^|[/.])(spam|junk)[^/.]*$/i.test(f)
    })
    .sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" }))
}

/** Der Papierkorb des Postfachs, falls es einen gibt (Name je Anbieter verschieden). */
export function papierkorb(alle: string[]): string | null {
  return alle.find((f) => /^(trash|papierkorb|gel(ö|oe)schte (objekte|elemente))$/i.test(f.split(/[/.]/).pop() || "")) || null
}

export function useOrdner(tenant: string, postfach: string) {
  return useQuery<string[]>({
    queryKey: ["imap-folders", tenant, postfach],
    queryFn: async () => {
      const r = await fetch(`/api/v1/folders?tenant=${encodeURIComponent(tenant)}${postfachQuery(postfach)}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      return Array.isArray(d) ? d as string[] : []
    },
    enabled: !!tenant,
    staleTime: 5 * 60_000,
  })
}

/** Ziele zum Verschieben: Posteingang, eigene Ordner, Papierkorb. */
export function ablageZiele(alle: string[]): string[] {
  const pk = papierkorb(alle)
  return ["INBOX", ...sichtbareOrdner(alle), ...(pk ? [pk] : [])]
}

async function sammel(ids: number[], action: "move" | "done", folder = ""): Promise<void> {
  const r = await fetch("/api/v1/emails/bulk", {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, action, folder }),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(j.detail || j.error || `HTTP ${r.status}`)
  if ((j.geaendert ?? ids.length) < ids.length) throw new Error(`${j.geaendert} von ${ids.length} geändert`)
}

/** Nimmt Mails aus allen geladenen Töpfen (Ein Klick, Braucht dich, Zur
 *  Kenntnis). Ordnerlisten bleiben: dort steht eine erledigte Mail weiter. */
export function toepfeOhne(qc: QueryClient, ids: number[]) {
  const weg = new Set(ids)
  qc.setQueriesData({ queryKey: ["emails", "toepfe"] }, (alt: unknown) => {
    if (!alt || typeof alt !== "object") return alt
    const t = { ...(alt as Record<string, unknown>) }
    for (const k of ["klick", "hand", "kenntnis"]) {
      const liste = t[k]
      if (Array.isArray(liste)) t[k] = liste.filter((z: { id: number }) => !weg.has(z.id))
    }
    return t
  })
}

export function useSammelaktion(nachher: () => void) {
  const qc = useQueryClient()
  return async (ids: number[], action: "move" | "done", folder = "") => {
    if (!ids.length) return
    const n = ids.length === 1 ? "1 Mail" : `${ids.length} Mails`
    // Paul 03.11.2026: „mehrere erledigen dauert so lange“ — die Zeilen
    // verschwinden sofort aus den Töpfen, die Anfrage läuft daneben.
    nachher()
    toepfeOhne(qc, ids)
    try {
      await sammel(ids, action, folder)
      qc.invalidateQueries({ queryKey: ["emails"] })
      if (action === "done") { toast(`✓ ${n} erledigt`); return }
      // Die Töpfe zeigen nur den Posteingang — dorthin geht der Rückweg.
      toast(`✓ ${n} → ${ordnerName(folder)}`, "success", folder === "INBOX" ? undefined : {
        label: "Rückgängig",
        onClick: () => {
          sammel(ids, "move", "INBOX")
            .then(() => { toast("Zurück im Posteingang"); qc.invalidateQueries({ queryKey: ["emails"] }) })
            .catch((e) => toast("Rückgängig fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"))
        },
      })
    } catch (e) {
      qc.invalidateQueries({ queryKey: ["emails"] })
      toast(`${action === "done" ? "Erledigen" : "Verschieben"} fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, "error")
    }
  }
}

/** Eine Zeile mit Haken zum Auswählen, ziehbar auf einen Ordner. Wird eine
 *  ausgewählte Zeile gezogen, wandert die ganze Auswahl mit. */
export function Waehlbar({ id, ordner, gewaehlt, auswahl, onHaken, onZiehen, children }: {
  id: number; ordner?: string | null; gewaehlt: boolean; auswahl: number[]
  onHaken: (id: number) => void; onZiehen: (an: boolean) => void; children: React.ReactNode
}) {
  return (
    <div className={gewaehlt ? "pk-wahl an" : "pk-wahl"} draggable
      onDragStart={(ev) => {
        const ids = gewaehlt && auswahl.length ? auswahl : [id]
        ev.dataTransfer.effectAllowed = "move"
        ev.dataTransfer.setData("application/x-email-ids", JSON.stringify(ids))
        ev.dataTransfer.setData("text/plain", String(id))
        ev.dataTransfer.setData(HERKUNFT_MIME, ordner || "INBOX")
        onZiehen(true)
      }}
      onDragEnd={() => onZiehen(false)}
      title="Ziehen zum Verschieben in einen Ordner">
      <button type="button" role="checkbox" aria-checked={gewaehlt} aria-label="Mail auswählen"
        className="pk-haken" onClick={() => onHaken(id)}>
        <span className="pk-haken-kasten"><Check className="w-3 h-3" aria-hidden /></span>
      </button>
      {children}
    </div>
  )
}

/** Blauer Punkt wie im Mailprogramm. */
export function UngelesenPunkt({ zeile }: { zeile: { gelesen?: boolean; email_direction?: string | null } }) {
  if (!istUngelesen(zeile)) return null
  return <span className="pk-ungelesen-punkt" role="img" aria-label="ungelesen" title="Ungelesen" data-testid="ungelesen" />
}

export function Anhang({ da }: { da?: boolean | null }) {
  if (!da) return null
  return <Paperclip className="w-3.5 h-3.5 pk-anhang" aria-label="mit Anhang" />
}

/** Leiste über den Töpfen, sobald Mails angehakt sind. */
export function AuswahlLeiste({ ids, ziele, onAktion, onAufheben, onGelesen }: {
  ids: number[]; ziele: string[]
  onAktion: (action: "move" | "done", folder?: string) => void; onAufheben: () => void
  /** Gelesen/ungelesen für die Auswahl — wirkt auch im Postfach. */
  onGelesen?: (gelesen: boolean) => void
}) {
  const [menue, setMenue] = useState(false)
  const [filter, setFilter] = useState("")
  useEffect(() => { if (!ids.length) setMenue(false) }, [ids.length])
  if (!ids.length) return null
  const liste = filter ? ziele.filter((z) => ordnerName(z).toLowerCase().includes(filter.toLowerCase())) : ziele
  return (
    <div className="pk-auswahl" role="region" aria-label="Auswahl" data-testid="auswahl-leiste">
      <div className="pk-auswahl-zeile">
        <b>{ids.length === 1 ? "1 Mail ausgewählt" : `${ids.length} Mails ausgewählt`}</b>
        <button type="button" className="pk-auswahl-knopf hell" aria-expanded={menue} onClick={() => { setMenue(!menue); setFilter("") }}>
          Verschieben nach …
        </button>
        <button type="button" className="pk-auswahl-knopf" onClick={() => onAktion("done")}>Erledigt</button>
        {onGelesen && <>
          <button type="button" className="pk-auswahl-knopf hell" onClick={() => onGelesen(true)} data-testid="auswahl-gelesen">Gelesen</button>
          <button type="button" className="pk-auswahl-knopf hell" onClick={() => onGelesen(false)} data-testid="auswahl-ungelesen">Ungelesen</button>
        </>}
        <button type="button" className="pk-auswahl-knopf leise" onClick={onAufheben} aria-label="Auswahl aufheben"><X className="w-4 h-4" aria-hidden /></button>
      </div>
      {menue && (
        <div className="pk-auswahl-menue">
          {ziele.length > 8 && (
            <input autoFocus value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Ordner suchen …"
              aria-label="Ordner suchen" onKeyDown={(e) => { if (e.key === "Enter" && liste[0]) onAktion("move", liste[0]) }} />
          )}
          <div className="pk-auswahl-ziele">
            {liste.map((z) => (
              <button key={z} type="button" onClick={() => onAktion("move", z)} title={z}>{ordnerName(z)}</button>
            ))}
          </div>
        </div>
      )}
      <span className="pk-auswahl-tipp">oder auf einen Ordner links ziehen</span>
    </div>
  )
}

function OrdnerZiel({ name, label, icon, aktiv, bereit, ablegbar, onClick, onAbgelegt }: {
  name: string; label: string; icon: React.ReactNode; aktiv: boolean; bereit: boolean; ablegbar: boolean
  onClick: () => void; onAbgelegt: () => void
}) {
  const [drueber, setDrueber] = useState(false)
  const qc = useQueryClient()
  const cls = ["pk-nav-zeile", aktiv && "aktiv", ablegbar && bereit && "bereit", drueber && "drueber"].filter(Boolean).join(" ")
  return (
    <button type="button" className={cls} onClick={onClick} title={name}
      onDragOver={ablegbar ? (ev) => { ev.preventDefault(); setDrueber(true) } : undefined}
      onDragLeave={() => setDrueber(false)}
      onDrop={ablegbar ? async (ev) => {
        ev.preventDefault()
        setDrueber(false)
        await verschiebenMitUndo(ev, name, () => { qc.invalidateQueries({ queryKey: ["emails"] }); onAbgelegt() })
      } : undefined}>
      <span className="pk-nav-icon" aria-hidden>{icon}</span>
      <span className="pk-nav-name">{label}</span>
    </button>
  )
}

/** Die Spalte links: Ansichten, alle Ordner, Ordner verwalten. */
export function OrdnerSpalte({ tenant, postfach, aktiv, auswahl, zieht, onWahl, onVerschieben, onAbgelegt }: {
  tenant: string; postfach: string; aktiv: Liste | null; auswahl: number[]; zieht: boolean
  onWahl: (l: Liste) => void
  /** Klick auf einen Ordner, während Mails angehakt sind: dorthin verschieben. */
  onVerschieben: (ordner: string) => void
  onAbgelegt: () => void
}) {
  const [zu, setZu] = useState(() => { try { return localStorage.getItem(ZU) === "1" } catch { return false } })
  const [verwalten, setVerwalten] = useState(false)
  const umschalten = () => {
    setZu(!zu)
    try { localStorage.setItem(ZU, zu ? "0" : "1") } catch { /* privat */ }
  }
  const zahlen = useQuery<Record<string, unknown>>({
    queryKey: ["emails", "ansichten-zahlen", tenant, postfach],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/ansichten/zahlen?tenant=${encodeURIComponent(tenant)}${postfachQuery(postfach)}`,
        { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: !!tenant,
    staleTime: 60_000,
  })
  const ordnerQ = useOrdner(tenant, postfach)
  const alle = ordnerQ.data || []
  const eigene = useMemo(() => sichtbareOrdner(alle), [alle])
  const pk = useMemo(() => papierkorb(alle), [alle])
  const zahl = (k: string) => (typeof zahlen.data?.[k] === "number" ? zahlen.data[k] as number : null)
  const bereit = zieht || auswahl.length > 0

  if (zu && !zieht) {
    return (
      <div className="pk-ordner-zu">
        <button type="button" className="pk-ordner-auf" onClick={umschalten} title="Ordner einblenden" aria-label="Ordner einblenden"
          data-testid="ordner-einblenden">
          <Folder className="w-[18px] h-[18px]" aria-hidden />
        </button>
        <span className="pk-ordner-senkrecht">Ordner</span>
      </div>
    )
  }

  const ansichten: { liste: Liste; icon: React.ReactNode; zahl: number | null }[] = [
    { liste: { art: "meine", titel: "Mir zugewiesen" }, icon: <UserRound className="w-4 h-4" />, zahl: zahl("meine") },
    { liste: { art: "wartet", titel: "Wartet auf Kunde" }, icon: <Clock className="w-4 h-4" />, zahl: zahl("wartet") },
    { liste: { art: "gesendet", titel: "Gesendet" }, icon: <Send className="w-4 h-4" />, zahl: null },
  ]
  const klick = (l: Liste) => {
    if (l.art === "ordner" && auswahl.length && l.ordner !== SPAM) onVerschieben(l.ordner)
    else onWahl(l)
  }
  const ziel = (ordner: string, titel: string, icon: React.ReactNode, ablegbar = true) => {
    const l: Liste = { art: "ordner", ordner, titel }
    return (
      <OrdnerZiel key={ordner} name={ordner} label={titel} icon={icon} aktiv={listeGleich(aktiv, l)}
        bereit={bereit} ablegbar={ablegbar} onClick={() => klick(l)} onAbgelegt={onAbgelegt} />
    )
  }

  return (
    <nav className={zu ? "pk-ordner schwebt" : "pk-ordner"} aria-label="Ordner und Ansichten" data-testid="ordner-ansichten">
      <div className="pk-ordner-kopf">
        <h2 className="pk-topf-titel leise"><span>Ansichten</span></h2>
        <button type="button" className="pk-ordner-zuknopf" onClick={umschalten} title="Ordner ausblenden, mehr Platz"
          aria-label="Ordner ausblenden" data-testid="ordner-ausblenden">
          <ChevronLeft className="w-4 h-4" aria-hidden />
        </button>
      </div>
      {ansichten.map(({ liste, icon, zahl: z }) => (
        <button key={liste.art} type="button" className={listeGleich(aktiv, liste) ? "pk-nav-zeile aktiv" : "pk-nav-zeile"}
          onClick={() => onWahl(liste)}>
          <span className="pk-nav-icon" aria-hidden>{icon}</span>
          <span className="pk-nav-name">{liste.titel}</span>
          {z != null && z > 0 && <span className="pk-nav-zahl">{z}</span>}
        </button>
      ))}
      <h3 className="pk-ordner-titel">Ordner</h3>
      {bereit && <p className="pk-ordner-hinweis">{zieht ? "Auf einen Ordner fallen lassen" : "Klick auf einen Ordner verschiebt die Auswahl"}</p>}
      {ordnerQ.isError && <p className="pk-fehler">Die Ordner konnten nicht geladen werden.</p>}
      {ziel("INBOX", "Posteingang", <Inbox className="w-4 h-4" />)}
      {eigene.map((f) => ziel(f, f, <Folder className="w-4 h-4" />))}
      {pk && ziel(pk, "Papierkorb", <Trash2 className="w-4 h-4" />)}
      {ziel(SPAM, "Spam", <ShieldAlert className="w-4 h-4" />, false)}
      <button type="button" className="pk-link pk-nav-verwalten" onClick={() => setVerwalten(true)}>Ordner verwalten</button>
      {verwalten && <OrdnerVerwalten tenant={tenant} postfach={postfach} ordner={eigene} onZu={() => setVerwalten(false)} />}
    </nav>
  )
}

/** Ordner anlegen, umbenennen, löschen — dieselben Wege wie die bisherige
 *  Ordnerleiste (`/api/v1/folders`), wirken im echten Postfach. */
function OrdnerVerwalten({ tenant, postfach, ordner, onZu }: {
  tenant: string; postfach: string; ordner: string[]; onZu: () => void
}) {
  const qc = useQueryClient()
  const [neu, setNeu] = useState("")
  const [umbenennen, setUmbenennen] = useState<{ alt: string; neu: string } | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const pf = postfach && postfach !== "haupt" ? postfach : null
  const senden = async (method: "POST" | "PATCH" | "DELETE", body: Record<string, unknown>, ok: string) => {
    setLaeuft(true)
    try {
      const r = await fetch("/api/v1/folders", {
        method, credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant, postfach: pf, ...body }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`)
      toast(ok)
      qc.invalidateQueries({ queryKey: ["imap-folders"] })
      return true
    } catch (e) {
      toast("Fehler: " + (e instanceof Error ? e.message : String(e)), "error")
      return false
    } finally {
      setLaeuft(false)
    }
  }
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onZu() }
    window.addEventListener("keydown", esc)
    return () => window.removeEventListener("keydown", esc)
  }, [onZu])
  return (
    <div className="pk-dialog-hintergrund" onClick={onZu}>
      <div className="pk-dialog" role="dialog" aria-modal="true" aria-label="Ordner verwalten" onClick={(e) => e.stopPropagation()}>
        <div className="pk-dialog-kopf">
          <h2>Ordner verwalten</h2>
          <button type="button" className="pk-knopf-leise" onClick={onZu} aria-label="Schließen"><X className="w-4 h-4" aria-hidden /></button>
        </div>
        <p className="pk-leise">Änderungen gelten im echten Postfach, also auch in Outlook und auf dem Handy.</p>
        <form className="pk-dialog-zeile" onSubmit={async (e) => {
          e.preventDefault()
          const n = neu.trim()
          if (n && await senden("POST", { name: n }, `✓ Ordner „${n}“ angelegt`)) setNeu("")
        }}>
          <input value={neu} onChange={(e) => setNeu(e.target.value)} placeholder="Neuer Ordner, z. B. Baustellen 2027" aria-label="Name des neuen Ordners" />
          <button type="submit" className="pk-knopf" disabled={laeuft || !neu.trim()}>Anlegen</button>
        </form>
        <div className="pk-dialog-liste">
          {ordner.length === 0 && <p className="pk-leise">Noch keine eigenen Ordner.</p>}
          {ordner.map((o) => umbenennen?.alt === o ? (
            <form key={o} className="pk-dialog-zeile" onSubmit={async (e) => {
              e.preventDefault()
              const n = umbenennen.neu.trim()
              if (!n || n === o) { setUmbenennen(null); return }
              if (await senden("PATCH", { old_name: o, new_name: n }, "✓ Umbenannt")) setUmbenennen(null)
            }}>
              <input autoFocus value={umbenennen.neu} onChange={(e) => setUmbenennen({ alt: o, neu: e.target.value })} aria-label={`Neuer Name für ${o}`} />
              <button type="submit" className="pk-knopf" disabled={laeuft}>Speichern</button>
              <button type="button" className="pk-knopf-leise" onClick={() => setUmbenennen(null)}>Abbrechen</button>
            </form>
          ) : (
            <div key={o} className="pk-dialog-zeile">
              <Folder className="w-4 h-4 pk-leise" aria-hidden />
              <span className="pk-nav-name" title={o}>{o}</span>
              <button type="button" className="pk-knopf-leise" onClick={() => setUmbenennen({ alt: o, neu: o })} aria-label={`${o} umbenennen`}>
                <Pencil className="w-4 h-4" aria-hidden />
              </button>
              <button type="button" className="pk-knopf-leise rot" disabled={laeuft} aria-label={`${o} löschen`}
                onClick={() => { if (confirm(`Ordner „${o}“ löschen? Das geht nur, wenn er leer ist.`)) senden("DELETE", { name: o }, "✓ Gelöscht") }}>
                <Trash2 className="w-4 h-4" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Die gewählte Ansicht bzw. der Ordner als Liste an Stelle der Töpfe. */
export function ListenAnsicht({ tenant, postfach, liste, aktiv, onOeffnen, onZurueck, wahl }: {
  tenant: string; postfach: string; liste: Liste; aktiv: number | null
  onOeffnen: (id: number) => void; onZurueck: () => void
  wahl: { auswahl: number[]; onHaken: (id: number) => void; onZiehen: (an: boolean) => void }
}) {
  const q = useQuery({
    queryKey: ["emails", "pk-liste", tenant, postfach, liste.art, liste.art === "ordner" ? liste.ordner : ""],
    queryFn: () => liste.art === "gesendet"
      ? fetchEmails(tenant, undefined, "sent", postfach || undefined, 200)
      : liste.art === "ordner"
        ? fetchEmails(tenant, undefined, undefined, postfach || undefined, 200, undefined, liste.ordner)
        : fetchEmails(tenant, undefined, undefined, postfach || undefined, 200, liste.art),
    enabled: !!tenant,
  })
  const items = q.data?.items || []
  const leer = liste.art === "meine" ? "Dir ist gerade nichts zugewiesen."
    : liste.art === "wartet" ? "Gerade wartet nichts auf eine Antwort."
    : liste.art === "gesendet" ? "Noch nichts gesendet." : "Dieser Ordner ist leer."
  return (
    <div className="pk-topf" data-testid="pk-liste">
      <h2 className="pk-topf-titel leise">
        <span>{liste.titel}</span>
        <span>{q.data ? (items.length >= 200 ? "200+" : items.length) : "…"}</span>
      </h2>
      <button type="button" className="pk-link" style={{ alignSelf: "flex-start", marginBottom: 4 }} onClick={onZurueck}>
        Zurück zu den Töpfen
      </button>
      {q.isError && <p className="pk-fehler">Die Liste konnte nicht geladen werden. Bitte noch einmal versuchen.</p>}
      {items.map((z) => (
        <Waehlbar key={z.id} id={z.id} ordner={z.imap_folder} gewaehlt={wahl.auswahl.includes(z.id)} auswahl={wahl.auswahl}
          onHaken={wahl.onHaken} onZiehen={wahl.onZiehen}>
          <button type="button" className={zeilenKlasse("pk-zeile", aktiv === z.id, z)} onClick={() => onOeffnen(z.id)}>
            <span className="pk-zeile-kopf">
              <UngelesenPunkt zeile={z} />
              <b data-pii>{z.email_direction === "sent" ? `An ${cleanSender(z.recipient) || "—"}` : cleanSender(z.sender) || z.sender_email || "—"}</b>
              <span className="pk-zeit">{fmtDate(z.received_at)}</span>
            </span>
            <span className="pk-zeile-betreff"><span data-pii>{z.subject || "(ohne Betreff)"}</span><Anhang da={z.has_attachments} /></span>
            {liste.art !== "ordner" && z.imap_folder && <span className="pk-leise" style={{ fontSize: 13 }}>{ordnerName(z.imap_folder)}</span>}
          </button>
        </Waehlbar>
      ))}
      {q.data && items.length === 0 && <p className="pk-leise pk-topf-leer">{leer}</p>}
      {items.length >= 200 && <p className="pk-leise pk-topf-leer">Ältere findest du über die Suche oben.</p>}
    </div>
  )
}
